import React, { useState } from 'react'
import { MeetingTemplatePreset } from '../../../store/ai/meetingTemplate'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { useGetFeaturesQuery, useGetPresetsQuery } from '../../../store/ai/ai'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import classNames from 'classnames'
import { MeetingTemplatePresetIconWrapper } from './MeetingTemplateView'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { AIFeaturePresetBusinessEvents } from '../AIFeaturePresets/AIFeaturePresetBusinessEvents'

export interface AddMeetingTemplatePresetModalProps {
  show: boolean
  onSave: (addedPresets: MeetingTemplatePreset[], deletedPresetIds: string[]) => void
  onCancel: () => void
  currentPresetIds: string[]
}

export const AddMeetingTemplatePresetModal: React.FC<AddMeetingTemplatePresetModalProps> = ({ show, onSave, onCancel, currentPresetIds }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: presetData, isFetching: isFetchingPresetData } = useGetPresetsQuery({ accountId, pageRequest: { page: 0, size: 500 } })
  const { data: featureData, isFetching: isFetchingFeatureData } = useGetFeaturesQuery({
    accountId: accountId,
    pageRequest: { page: 0, size: 500 },
  })

  const [newPresets, setNewPresets] = useState<MeetingTemplatePreset[]>([])
  const [deletedPresetIds, setDeletedPresetIds] = useState<string[]>([])
  const { getQuotaValue } = useZocksQuota()
  const maxPresetsInMeetingTemplate = getQuotaValue('ai.maxPresetsInMeetingTemplate.value') ?? 0
  const activePresetCount = currentPresetIds.length + newPresets.length - deletedPresetIds.length
  const canAddPreset = activePresetCount < maxPresetsInMeetingTemplate

  const handleAddPreset = (addedPresetId: string) => {
    if (currentPresetIds.includes(addedPresetId)) {
      setDeletedPresetIds((deletedPresetIds) => deletedPresetIds.filter((presetId) => presetId !== addedPresetId))
      return
    }
    const aiFeature = featureData?.data.find((feature) => feature.preset?.id === addedPresetId)
    setNewPresets((presetsToAdd) => [...presetsToAdd, { aiFeaturePresetId: addedPresetId, aiFeatureId: aiFeature?.id, metadata: '' }])
  }

  const handleRemovePreset = (removedPresetId: string) => {
    setNewPresets((presetsToAdd) => presetsToAdd.filter((preset) => preset.aiFeaturePresetId !== removedPresetId))
    if (currentPresetIds.includes(removedPresetId)) {
      setDeletedPresetIds((deletedPresetIds) => [...new Set([...deletedPresetIds, removedPresetId])])
    }
  }

  const handleSave = () => {
    onSave(newPresets, deletedPresetIds)
  }

  const getBody = () => {
    if (!presetData || isFetchingPresetData || isFetchingFeatureData) {
      return (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )
    }
    return (
      <Container>
        <Row className='pb-3'>
          <Col>
            <h4 className='d-flex justify-content-center align-items-center'>Manage presets {`(${activePresetCount}/${maxPresetsInMeetingTemplate})`}</h4>
          </Col>
        </Row>
        <Row className='pb-4'>
          <Col className='p-0'>
            {presetData.data
              .filter((p) => p.installed)
              .sort((a, b) => (a.name < b.name ? -1 : 1))
              .map((aiFeaturePreset) => {
                const { id: presetId, name, assetUrl } = aiFeaturePreset
                const presetAdded =
                  newPresets.some((p) => p.aiFeaturePresetId === presetId) ||
                  (currentPresetIds.some((currentPresetId) => currentPresetId === presetId) &&
                    !deletedPresetIds.some((deletedPresetId) => deletedPresetId === presetId))
                return (
                  <div
                    key={presetId}
                    className={classNames('d-flex gap-3 p-3 border-top align-items-center justify-content-between', { addedPreset: presetAdded })}
                  >
                    <div className='d-flex gap-3 align-items-center'>
                      <ScopedBlock
                        authActions={[() => canAddPreset || presetAdded]}
                        missingScopeAction={MissingScopeAction.UPGRADE}
                        showUpgradeBadge={false}
                        tooltipText={
                          <b className='text-wrap'>
                            Your current plan has a limit of {maxPresetsInMeetingTemplate} presets in a meeting type.
                            <br />
                            Upgrade to add more presets.
                          </b>
                        }
                        tooltipButtonText={'Upgrade'}
                      >
                        <Form.Check
                          type='checkbox'
                          checked={presetAdded}
                          disabled={!canAddPreset && !presetAdded}
                          onChange={presetAdded ? () => handleRemovePreset(presetId) : () => handleAddPreset(presetId)}
                        />
                      </ScopedBlock>
                      <MeetingTemplatePresetIconWrapper>
                        <img src={assetUrl} alt='asset logo' />
                      </MeetingTemplatePresetIconWrapper>
                      <div className='lead-small text-nowrap'>{name}</div>
                    </div>
                    <AIFeaturePresetBusinessEvents aiFeaturePreset={aiFeaturePreset} className='align-items-end' />
                  </div>
                )
              })}
          </Col>
        </Row>
      </Container>
    )
  }
  return (
    <Modal
      className={'h-75'}
      show={show}
      onHide={onCancel}
      header={<></>}
      body={getBody()}
      showFooterActions={true}
      onFooterPrimaryAction={handleSave}
      footerPrimaryActionTitle='Change presets'
      scrollable
      size='lg'
    />
  )
}
